import React, { useEffect, useState } from 'react';
import { Button, Card, AnswerExplainer, QuestionDiagram, StepsList } from '../../shared/components';
import { api } from '../../utils/api';
import { noCopyHandlers } from '../../utils/noCopy';
import './Quizzes.css';

export default function QuizRunner({ topic, onExit }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mixed-practice topics (exam_weight_pct 0) have an exact, curated
    // 20-question pool, so ask for enough to get all of them; regular
    // by-domain topics draw from hundreds of questions and should stay a
    // standard 10-question quiz.
    const count = topic.exam_weight_pct === 0 ? 50 : 10;
    api
      .get(`/api/quizzes/${topic.slug}/questions?count=${count}`)
      .then((data) => {
        setQuestions(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load questions right now.');
        setLoading(false);
      });
  }, [topic.slug, topic.exam_weight_pct]);

  function selectAnswer(questionId, selectedIndex) {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedIndex }));
  }

  async function handleSubmit() {
    const payload = questions.map((q) => ({ questionId: q.id, selectedIndex: answers[q.id] }));
    try {
      const response = await api.post(`/api/quizzes/${topic.slug}/submit`, { answers: payload });
      setResult(response);
    } catch (err) {
      setError('Unable to submit your quiz right now.');
    }
  }

  if (loading) return <p>Loading questions&hellip;</p>;
  if (error) return <p className="quizzes-error">{error}</p>;

  if (result) {
    const resultsById = new Map(result.results.map((r) => [r.questionId, r]));
    return (
      <div className="quizzes-page">
        <h1>{topic.name} &mdash; Results</h1>
        <p className="quizzes-score">
          {result.correctCount} / {result.totalQuestions} correct
        </p>

        {questions.map((q) => {
          const r = resultsById.get(q.id);
          return (
            <Card
              key={q.id}
              className={`quiz-question-review quiz-no-copy ${r.correct ? 'quiz-question-review--correct' : 'quiz-question-review--incorrect'}`}
              {...noCopyHandlers}
            >
              <p className="quiz-question-text">{q.question_text}</p>
              <QuestionDiagram svg={q.image_svg} />
              <p className="quiz-question-answer">Correct answer: {q.choices[r.correctIndex]}</p>
              <StepsList steps={r.steps} />
              {r.explanation && <p className="quiz-question-explanation">{r.explanation}</p>}
              {!r.correct && (
                <AnswerExplainer
                  explanation={r.aiExplanation}
                  questionText={q.question_text}
                  choices={q.choices}
                  selectedIndex={answers[q.id]}
                  correctIndex={r.correctIndex}
                />
              )}
            </Card>
          );
        })}

        <Button onClick={onExit}>Back to topics</Button>
      </div>
    );
  }

  const allAnswered = questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="quizzes-page">
      <h1>{topic.name}</h1>
      {questions.map((q, index) => (
        <Card key={q.id} className="quiz-question-card quiz-no-copy" {...noCopyHandlers}>
          <p className="quiz-question-text">{index + 1}. {q.question_text}</p>
          <QuestionDiagram svg={q.image_svg} />
          <div className="quiz-choices">
            {q.choices.map((choice, i) => (
              <label key={i} className="quiz-choice">
                <input
                  type="radio"
                  name={`question-${q.id}`}
                  checked={answers[q.id] === i}
                  onChange={() => selectAnswer(q.id, i)}
                />
                {choice}
              </label>
            ))}
          </div>
        </Card>
      ))}

      <div className="quizzes-actions">
        <Button variant="secondary" onClick={onExit}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!allAnswered}>Submit quiz</Button>
      </div>
    </div>
  );
}
